import React from 'react';
import { Box, Typography, Button } from '@mui/material';

/**
 * Centered empty-state block for dashboards and lists.
 *
 * @param {string} title
 * @param {string} [subtitle]
 * @param {string} [actionLabel]
 * @param {() => void} [onAction]
 * @param {React.ReactNode} [startIcon] — e.g. MUI icon for the primary button
 * @param {React.ReactNode} [icon] — larger decorative icon above the title
 */
function EmptyState({
  title,
  subtitle,
  actionLabel,
  onAction,
  startIcon,
  icon,
}) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: theme.spacing(45), sm: '55vh' },
        py: 6,
        px: 2,
        textAlign: 'center',
      })}
    >
      {icon ? (
        <Box
          sx={{
            color: 'text.secondary',
            mb: 2,
            lineHeight: 0,
            opacity: 0.9,
            '& .MuiSvgIcon-root': {
              fontSize: (theme) => theme.spacing(8),
            },
          }}
        >
          {icon}
        </Box>
      ) : null}

      <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
        {title}
      </Typography>

      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: (theme) => theme.spacing(45) }}
        >
          {subtitle}
        </Typography>
      ) : null}

      {actionLabel && onAction ? (
        <Button
          variant="contained"
          color="primary"
          size="medium"
          startIcon={startIcon}
          onClick={onAction}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            transition: (theme) =>
              theme.transitions.create(['transform', 'box-shadow'], {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

export default EmptyState;
